import { Bot, LayoutDashboard, Box, Boxes, Activity, ArrowUpRight, TrendingUp } from 'lucide-react';
import { ControlArchetype } from '../components/archetypes/ControlArchetype';
import { useTranslation } from 'react-i18next';
import { useSignature } from '../contexts/SignatureContext';

import { useAuth } from '../contexts/AuthContext';

export function Dashboard() {
  const { t } = useTranslation();
  const { colors } = useSignature();
  const { getEmpresaNome, getEmpresaCnpj } = useAuth();

  const empresaNome = getEmpresaNome() || 'Operação Galatea';
  const empresaCnpj = getEmpresaCnpj();

  return (
    <ControlArchetype
      title={t('common.dashboard', 'Painel de Controle')}
      subtitle={`Empresa: ${empresaNome} ${empresaCnpj ? `(${empresaCnpj})` : ''} · Visão consolidada da operação`}
      icon={<LayoutDashboard className="w-5 h-5 text-[#2474A8]" />}
      headerActions={
        <button
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer shadow-xs border"
          style={{
            backgroundColor: colors.accentIA,
            borderColor: colors.border,
            color: '#FFFFFF',
          }}
        >
          <Bot className="w-4 h-4 text-purple-100" />
          <span>Assistente IA</span>
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Painel Principal: Indicadores & Dashboards Operacionais (2 colunas) */}
        <div
          className="lg:col-span-2 rounded-xl border overflow-hidden transition-colors"
          style={{
            backgroundColor: colors.bgSurface,
            borderColor: colors.border,
            color: colors.textPrimary,
          }}
        >
          <div
            className="p-4 border-b flex items-center justify-between"
            style={{
              borderColor: colors.border,
              backgroundColor: colors.bgSurfaceHover,
            }}
          >
            <div className="flex items-center gap-2">
              <Box className="w-4 h-4" style={{ color: colors.primary }} /> 
              <span className="font-bold text-xs sm:text-sm" style={{ color: colors.textPrimary }}>
                Painéis Ativos de Gestão
              </span>
            </div>
            <span
              className="text-[11px] font-bold px-2 py-0.5 rounded border"
              style={{
                backgroundColor: colors.primaryLight,
                color: colors.primary,
                borderColor: colors.border,
              }}
            >
              4 Painéis
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead
                className="uppercase text-[10px] font-bold tracking-wider border-b"
                style={{
                  backgroundColor: colors.bgWorkspace,
                  borderColor: colors.border,
                  color: colors.textSecondary,
                }}
              >
                <tr>
                  <th className="px-4 py-3 font-bold">Painel / Módulo</th>
                  <th className="px-4 py-3 font-bold">Última Atualização</th>
                  <th className="px-4 py-3 text-right font-bold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: colors.border }}>
                <tr className="transition-colors hover:opacity-90" style={{ backgroundColor: colors.bgSurface }}>
                  <td className="px-4 py-3 font-semibold flex items-center gap-2" style={{ color: colors.textPrimary }}>
                    <TrendingUp className="w-4 h-4 shrink-0" style={{ color: colors.success }} />
                    Visão Geral de Vendas & Faturamento
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.textSecondary }}>Hoje, 09:30</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      className="inline-flex items-center gap-1 font-bold text-xs transition-colors cursor-pointer hover:underline"
                      style={{ color: colors.primary }}
                    >
                      Abrir <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>

                <tr className="transition-colors hover:opacity-90" style={{ backgroundColor: colors.bgSurface }}>
                  <td className="px-4 py-3 font-semibold flex items-center gap-2" style={{ color: colors.textPrimary }}>
                    <Activity className="w-4 h-4 shrink-0" style={{ color: colors.warning }} />
                    Inadimplência, Títulos & Cobranças
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.textSecondary }}>Ontem, 18:00</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      className="inline-flex items-center gap-1 font-bold text-xs transition-colors cursor-pointer hover:underline"
                      style={{ color: colors.primary }}
                    >
                      Abrir <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>

                <tr className="transition-colors hover:opacity-90" style={{ backgroundColor: colors.bgSurface }}>
                  <td className="px-4 py-3 font-semibold flex items-center gap-2" style={{ color: colors.textPrimary }}>
                    <Boxes className="w-4 h-4 shrink-0 text-purple-600" />
                    Dashboard de Estoques e Atendimento (Galatea IA)
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.textSecondary }}>Tempo Real</td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href="/estoque"
                      className="inline-flex items-center gap-1 font-bold text-xs transition-colors cursor-pointer hover:underline text-purple-600"
                    >
                      Abrir <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </td>
                </tr>

                <tr className="transition-colors hover:opacity-90" style={{ backgroundColor: colors.bgSurface }}>
                  <td className="px-4 py-3 font-semibold flex items-center gap-2" style={{ color: colors.textPrimary }}>
                    <Box className="w-4 h-4 shrink-0" style={{ color: colors.primary }} />
                    Metas Mensais (Realizado vs Previsto)
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.textSecondary }}>25/08/2026</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      className="inline-flex items-center gap-1 font-bold text-xs transition-colors cursor-pointer hover:underline"
                      style={{ color: colors.primary }}
                    >
                      Abrir <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Card Galatea IA — Integrado à Paleta Galatea Signature (SUPERFÍCIE LIGHT #E8EFF5) */}
        <div
          className="rounded-xl border p-5 flex flex-col justify-between transition-colors shadow-xs"
          style={{
            backgroundColor: colors.bgSurface,
            borderColor: colors.border,
            color: colors.textPrimary,
          }}
        >
          <div>
            <div className="flex items-center justify-between mb-3 pb-2 border-b" style={{ borderColor: colors.divider }}>
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4" style={{ color: colors.accentIA }} />
                <span className="font-bold text-xs" style={{ color: colors.textPrimary }}>
                  Galatea IA Assistente
                </span>
              </div>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded border"
                style={{
                  backgroundColor: colors.accentIALight,
                  color: colors.accentIA,
                  borderColor: colors.border,
                }}
              >
                Oráculo Fiscal
              </span>
            </div>

            <h4 className="font-bold text-sm mb-1.5" style={{ color: colors.textPrimary }}>
              Auditoria Preventiva & Diagnóstico
            </h4>
            <p className="text-xs leading-relaxed mb-4" style={{ color: colors.textSecondary }}>
              Consulte regras fiscais, tributação de produtos, validação de NF-e e diagnósticos fiscais com o motor GalateaIA.
            </p>
          </div>

          <button
            className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer border shadow-xs"
            style={{
              backgroundColor: colors.accentIA,
              borderColor: colors.border,
              color: '#FFFFFF',
            }}
          >
            <Bot className="w-4 h-4 text-purple-100" /> Iniciar Consulta Inteligente
          </button>
        </div>
      </div>
    </ControlArchetype>
  );
}
